import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { Download } from 'lucide-react';
import type { ImportResult as Result } from '../api/subscribersApi';
import { downloadCsv, toCsv } from '../model/csvFile';

const SHOWN = 50;
const count = (n: number) => n.toLocaleString('en-SG');
const slug = (name: string) => name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '') || 'list';

/** What the import did: how many went in, each row left out with its reason, and both as CSV files. The "not
 * added" file keeps the rows as written plus a reason column, so it can be fixed and imported again as it is. */
export function ImportResult({ result, listName }: { result: Result; listName: string }) {
  const { added, imported, problems } = result;
  const file = slug(listName);
  return (
    <Stack sx={{ gap: 1.5 }}>
      <Alert severity={added > 0 ? 'success' : 'warning'}>
        {added === 1 ? '1 client added.' : `${count(added)} clients added.`}
        {problems.length > 0 ? ` ${count(problems.length)} ${problems.length === 1 ? 'row was' : 'rows were'} not added.` : ''}
      </Alert>
      <Stack direction="row" sx={{ gap: 1, flexWrap: 'wrap' }}>
        <Button
          variant="outlined"
          startIcon={<Download size={15} />}
          disabled={imported.length === 0}
          onClick={() => { downloadCsv(`${file}-added.csv`, toCsv(imported, ['name', 'phone', 'email'])); }}
        >
          Added ({count(imported.length)})
        </Button>
        <Button
          variant="outlined"
          color="warning"
          startIcon={<Download size={15} />}
          disabled={problems.length === 0}
          onClick={() => { downloadCsv(`${file}-not-added.csv`, toCsv(problems, ['row', 'name', 'phone', 'email', 'reason'])); }}
        >
          Not added ({count(problems.length)})
        </Button>
      </Stack>
      {problems.length > 0 ? (
        <>
          <Typography variant="subtitle2">Rows not added (fix them in the &quot;Not added&quot; file and import it again)</Typography>
          <List dense sx={{ maxHeight: 240, overflow: 'auto', border: 1, borderColor: 'divider', borderRadius: 1 }}>
            {problems.slice(0, SHOWN).map((p) => (
              <ListItem key={`${String(p.row)}-${p.reason}`}>
                <ListItemText primary={`Row ${String(p.row)}${p.name ? `: ${p.name}` : ''}`} secondary={p.reason} />
              </ListItem>
            ))}
          </List>
          {problems.length > SHOWN ? (
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>And {count(problems.length - SHOWN)} more, all in the file.</Typography>
          ) : null}
        </>
      ) : null}
    </Stack>
  );
}
